import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, FormControl, IconButton, InputLabel, MenuItem, Paper, Select,
  Tab, Tabs, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as RulesIcon,
  CheckCircleOutline as ReviewedIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileUpload as UploadIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  BlockOutlined as DismissIcon,
  TroubleshootOutlined as ScanIcon,
  Warning as WarnIcon,
} from '@mui/icons-material';
import { RootState, PlaybookTemplateType, RuleReviewStatus } from '../types';
import { fetchClients } from '../store/slices/clientsSlice';
import { PlaybooksService } from '../services/playbooksService';
import { colorUtils } from '../theme';
import { getRelativeTimeString } from '../utils/dateUtils';

const formatType = (type: string) =>
  type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ── Compliance framework options ─────────────────────────────────────────────
const FRAMEWORKS = [
  { value: 'nis2',        label: 'NIS2 Directive (EU 2022/2555)' },
  { value: 'gdpr',        label: 'GDPR (EU 2016/679)' },
  { value: 'sama',        label: 'SAMA Cyber Security Framework' },
  { value: 'nist_800_61', label: 'NIST SP 800-61' },
];

// ── JSON template example shown in the dialog ────────────────────────────────
const JSON_EXAMPLE = `{
  "name": "Phishing Response",
  "type": "phishing",
  "version": "1.0",
  "client_id": "<your-client-id>",
  "framework": "nis2",
  "ai_focus": "Focus on credential theft and lateral movement",
  "detection_signals": {
    "title_keywords": ["phishing", "malicious email"],
    "mitre_tactics": ["TA0001"],
    "mitre_techniques": ["T1566"],
    "categories": ["phishing"]
  },
  "steps": [
    {
      "phase": "detection",
      "action": "Monitor email security gateway for phishing alerts",
      "what_to_check": "Suspicious sender domains and attachment hashes",
      "expected_output": "Phishing email identified and quarantined",
      "conditions": "If email volume exceeds baseline by 20%",
      "responsible": "SOC",
      "step_type": "technical",
      "is_required": true
    }
  ]
}`;

// ── Import dialog ────────────────────────────────────────────────────────────

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: (template: PlaybookTemplateType) => void;
  clients: { id: string; name: string }[];
}

const ImportDialog: React.FC<ImportDialogProps> = ({ open, onClose, onImported, clients }) => {
  const theme = useTheme();
  const fileRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<'file' | 'json'>('file');
  const [clientId, setClientId] = useState('');
  const [framework, setFramework] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setTab('file');
    setClientId('');
    setFramework('');
    setFile(null);
    setJsonText('');
    setJsonError('');
    setError('');
    setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleImport = async () => {
    if (!clientId) { setError('Select a client first'); return; }
    setError('');
    setLoading(true);
    try {
      let created: PlaybookTemplateType;
      if (tab === 'file') {
        if (!file) { setError('Select a file to upload'); setLoading(false); return; }
        created = await PlaybooksService.importTemplateFromFile(file, clientId, framework || undefined);
      } else {
        let parsed: any;
        try {
          parsed = JSON.parse(jsonText);
          setJsonError('');
        } catch {
          setJsonError('Invalid JSON — fix the syntax before importing');
          setLoading(false);
          return;
        }
        created = await PlaybooksService.importTemplateFromJson({
          ...parsed,
          client_id: clientId,
          framework: framework || undefined,
        });
      }
      onImported(created);
      reset();
    } catch (e: any) {
      setError(e.message ?? 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Import Playbook Template</Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: `1px solid ${theme.palette.border.main}` }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="file" label="Upload document" sx={{ fontSize: '0.78rem', textTransform: 'none' }} />
          <Tab value="json" label="Import JSON" sx={{ fontSize: '0.78rem', textTransform: 'none' }} />
        </Tabs>

        {/* Client selector — common to both modes */}
        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Client *</InputLabel>
          <Select
            value={clientId}
            label="Client *"
            onChange={(e) => setClientId(e.target.value)}
            sx={{ fontSize: '0.82rem' }}
          >
            {clients.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.82rem' }}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Compliance framework selector */}
        <Tooltip title="Guides the AI to use the selected framework's phase taxonomy, notification obligations, and step classification rules. Leave blank for generic SOC best practices.">
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ fontSize: '0.82rem' }}>Compliance Framework (optional)</InputLabel>
            <Select
              value={framework}
              label="Compliance Framework (optional)"
              onChange={(e) => setFramework(e.target.value)}
              sx={{ fontSize: '0.82rem' }}
            >
              <MenuItem value="" sx={{ fontSize: '0.82rem' }}>
                <em>Generic SOC best practices</em>
              </MenuItem>
              {FRAMEWORKS.map((f) => (
                <MenuItem key={f.value} value={f.value} sx={{ fontSize: '0.82rem' }}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        {tab === 'file' ? (
          <>
            {/* Disclaimer */}
            <Box
              sx={{
                display: 'flex', gap: 1, p: 1.5, mb: 2,
                backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.08),
                border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.25)}`,
                borderRadius: 1,
              }}
            >
              <WarnIcon sx={{ fontSize: 16, color: theme.palette.warning.main, mt: 0.1, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                Extraction quality depends on document structure. The result will open in the editor
                so you can review and adjust before saving.
              </Typography>
            </Box>

            {/* File picker */}
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.doc,.txt,.md"
              style={{ display: 'none' }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon sx={{ fontSize: 15 }} />}
                onClick={() => fileRef.current?.click()}
                sx={{ fontSize: '0.78rem' }}
              >
                Choose file
              </Button>
              {file ? (
                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                  {file.name} ({(file.size / 1024).toFixed(0)} KB)
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.disabled }}>
                  PDF, DOCX, or TXT — max ~500 KB
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.tertiary, mb: 1 }}>
              Paste a JSON object matching the schema below. The <code>client_id</code> field is
              overridden by the client selector above.
            </Typography>
            <TextField
              multiline
              rows={12}
              fullWidth
              size="small"
              placeholder={JSON_EXAMPLE}
              value={jsonText}
              onChange={(e) => { setJsonText(e.target.value); setJsonError(''); }}
              error={!!jsonError}
              helperText={jsonError || undefined}
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem' }, mb: 1 }}
            />
          </>
        )}

        {error && (
          <Typography sx={{ fontSize: '0.78rem', color: theme.palette.error.main, mt: 0.5 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button size="small" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleImport}
            disabled={loading || (tab === 'file' ? !file : !jsonText.trim())}
            startIcon={loading ? <CircularProgress size={13} color="inherit" /> : undefined}
            sx={{ fontSize: '0.78rem' }}
          >
            {loading
              ? tab === 'file' ? 'Extracting…' : 'Importing…'
              : 'Import'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const TYPE_CHIP_COLOR: Record<string, string> = {
  ddos: '#e57373',
  ransomware: '#ffb74d',
  phishing: '#4fc3f7',
  insider_threat: '#ce93d8',
  data_breach: '#a5d6a7',
};

const TemplateRow: React.FC<{
  template: PlaybookTemplateType;
  clientName?: string;
  onEdit: () => void;
  onDelete: () => void;
  onGenerateRules: () => void;
}> = ({ template, clientName, onEdit, onDelete, onGenerateRules }) => {
  const theme = useTheme();
  const [deleting, setDeleting] = useState(false);
  const chipColor = TYPE_CHIP_COLOR[template.type] ?? theme.palette.primary.main;

  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete(); } finally { setDeleting(false); }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.border.light}`,
        '&:last-child': { borderBottom: 'none' },
        opacity: template.is_builtin ? 0.85 : 1,
      }}
    >
      {/* Type chip */}
      <Chip
        label={formatType(template.type)}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.62rem',
          fontWeight: 700,
          color: chipColor,
          backgroundColor: colorUtils.withOpacity(chipColor, 0.12),
          border: `1px solid ${colorUtils.withOpacity(chipColor, 0.3)}`,
          flexShrink: 0,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 },
        }}
      />

      {/* Name + meta */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{template.name}</Typography>
          {template.is_builtin && (
            <Chip label="built-in" size="small" sx={{ height: 16, fontSize: '0.58rem', opacity: 0.7 }} />
          )}
        </Box>
        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
          {template.steps_count ?? 0} steps
          {template.version ? ` · v${template.version}` : ''}
          {clientName ? ` · ${clientName}` : ''}
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {template.is_builtin ? (
          <Tooltip title="Built-in templates are read-only">
            <LockIcon sx={{ fontSize: 15, color: theme.palette.text.disabled, mr: 0.5 }} />
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Generate AI rules from this playbook">
              <IconButton size="small" onClick={onGenerateRules} sx={{ color: theme.palette.primary.main }}>
                <RulesIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit template">
              <IconButton size="small" onClick={onEdit} sx={{ color: theme.palette.text.secondary }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete template">
              <IconButton size="small" onClick={handleDelete} disabled={deleting} sx={{ color: theme.palette.error.main }}>
                {deleting ? <CircularProgress size={13} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

const RuleReviewRow: React.FC<{
  review: RuleReviewStatus;
  clientName?: string;
  onResolve: (status: 'reviewed' | 'dismissed') => Promise<void>;
}> = ({ review, clientName, onResolve }) => {
  const theme = useTheme();
  const [resolving, setResolving] = useState<'reviewed' | 'dismissed' | null>(null);

  const [editing, setEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editJson, setEditJson] = useState('');
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleResolve = async (status: 'reviewed' | 'dismissed') => {
    setResolving(status);
    try { await onResolve(status); } finally { setResolving(null); }
  };

  const handleStartEdit = async () => {
    setEditing(true);
    setEditError('');
    setSaved(false);
    setEditLoading(true);
    try {
      const rule = await PlaybooksService.getFlaggedRuleDefinition(review.id);
      setEditJson(JSON.stringify(rule, null, 2));
    } catch (e: any) {
      setEditError(e.message ?? 'Failed to load rule');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    let parsed: object;
    try {
      parsed = JSON.parse(editJson);
    } catch {
      setEditError('Invalid JSON — fix before saving');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      await PlaybooksService.saveFlaggedRuleOverride(review.id, parsed);
      setSaved(true);
      setEditing(false);
    } catch (e: any) {
      setEditError(e.message ?? 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const fpPct = review.false_positive_rate != null ? Math.round(review.false_positive_rate * 100) : null;

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.palette.border.light}`,
        '&:last-child': { borderBottom: 'none' },
        opacity: review.status === 'open' ? 1 : 0.6,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
        <Chip
          label={fpPct != null ? `${fpPct}% FP` : '—'}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.62rem',
            fontWeight: 700,
            color: theme.palette.error.main,
            backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.12),
            border: `1px solid ${colorUtils.withOpacity(theme.palette.error.main, 0.3)}`,
            flexShrink: 0,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {review.rule_name || review.rule_id}
            </Typography>
            {review.status !== 'open' && (
              <Chip
                label={review.status}
                size="small"
                sx={{ height: 16, fontSize: '0.58rem', textTransform: 'capitalize', opacity: 0.8 }}
              />
            )}
            {saved && (
              <Chip
                label="override saved"
                size="small"
                sx={{ height: 16, fontSize: '0.58rem', color: theme.palette.success.main, backgroundColor: colorUtils.withOpacity(theme.palette.success.main, 0.12) }}
              />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontFamily: 'monospace' }}>
            {review.rule_id}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block' }}>
            {review.false_positive_count}/{review.fire_count} fires flagged false positive
            {clientName ? ` · ${clientName}` : ''}
            {review.last_fired_at ? ` · last fired ${getRelativeTimeString(review.last_fired_at)}` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
          <Tooltip title={editing ? 'Cancel edit' : 'Edit rule — saves as an override for this client'}>
            <IconButton
              size="small"
              onClick={() => (editing ? setEditing(false) : handleStartEdit())}
              sx={{ color: editing ? theme.palette.text.secondary : theme.palette.primary.main }}
            >
              {editing ? <CloseIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          {review.status === 'open' && (
            <>
              <Tooltip title="Mark reviewed — rule is fine as-is, or fix tracked elsewhere">
                <IconButton size="small" disabled={resolving !== null} onClick={() => handleResolve('reviewed')} sx={{ color: theme.palette.success.main }}>
                  {resolving === 'reviewed' ? <CircularProgress size={14} color="inherit" /> : <ReviewedIcon sx={{ fontSize: 17 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Dismiss — not a real issue">
                <IconButton size="small" disabled={resolving !== null} onClick={() => handleResolve('dismissed')} sx={{ color: theme.palette.text.secondary }}>
                  {resolving === 'dismissed' ? <CircularProgress size={14} color="inherit" /> : <DismissIcon sx={{ fontSize: 17 }} />}
                </IconButton>
              </Tooltip>
            </>
          )}
          {review.status !== 'open' && review.reviewed_at && (
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
              {getRelativeTimeString(review.reviewed_at)}
            </Typography>
          )}
        </Box>
      </Box>

      {editing && (
        <Box sx={{ px: 2, pb: 2 }}>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={20}
                value={editJson}
                onChange={(e) => { setEditJson(e.target.value); setEditError(''); }}
                error={!!editError}
                helperText={editError || "Edit conditions/actions directly. Saves as this client's override — the global rule and other clients are unaffected."}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.76rem' } }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button size="small" variant="contained" disabled={saving} onClick={handleSaveEdit} startIcon={saving ? <CircularProgress size={12} color="inherit" /> : undefined}>
                  {saving ? 'Saving…' : 'Save override'}
                </Button>
                <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

const PlaybookTemplatesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clients } = useSelector((state: RootState) => state.clients);

  const [templates, setTemplates] = useState<PlaybookTemplateType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const [ruleReviews, setRuleReviews] = useState<RuleReviewStatus[]>([]);
  const [ruleReviewsLoading, setRuleReviewsLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showResolvedReviews, setShowResolvedReviews] = useState(false);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c.name]));

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await PlaybooksService.getTemplates();
      setTemplates(res.templates);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadRuleReviews = async () => {
    setRuleReviewsLoading(true);
    try {
      const res = await PlaybooksService.getRuleReviews();
      setRuleReviews(res.reviews);
    } catch {
      setRuleReviews([]);
    } finally {
      setRuleReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
    load();
    loadRuleReviews();
  }, []);

  const handleDelete = async (id: string) => {
    await PlaybooksService.deleteTemplate(id);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const handleScanRules = async () => {
    setScanning(true);
    try {
      await PlaybooksService.scanRuleReviews();
      await loadRuleReviews();
    } finally {
      setScanning(false);
    }
  };

  const handleResolveRuleReview = async (id: string, status: 'reviewed' | 'dismissed') => {
    const updated = await PlaybooksService.resolveRuleReview(id, status);
    setRuleReviews((prev) => prev.map((r) => (r.id === id ? updated : r)));
  };

  const builtin = templates.filter((t) => t.is_builtin);
  const custom = templates.filter((t) => !t.is_builtin);
  const openRuleReviews = ruleReviews.filter((r) => r.status === 'open');
  const resolvedRuleReviews = ruleReviews.filter((r) => r.status !== 'open');
  const visibleRuleReviews = [...openRuleReviews, ...(showResolvedReviews ? resolvedRuleReviews : [])];

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', pt: 10, pb: 3, px: { xs: 2, sm: 3, md: 4 } }}>
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        clients={clients}
        onImported={(t) => {
          setTemplates((prev) => [t, ...prev]);
          setImportOpen(false);
          navigate(`/playbooks/templates/${t.id}/edit`);
        }}
      />

      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}>
            Playbook Templates
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
            Manage response workflow templates. Custom templates can auto-generate detection rules via AI.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} sx={{ color: theme.palette.text.secondary }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => setImportOpen(true)}
            sx={{ fontSize: '0.78rem', height: 34 }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/playbooks/templates/new')}
            sx={{ fontSize: '0.78rem', height: 34 }}
          >
            New Template
          </Button>
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Typography sx={{ color: theme.palette.error.main, mb: 2, fontSize: '0.82rem' }}>{error}</Typography>
      )}

      {/* Rules flagged for review — misfiring detection rules (high FP rate) */}
      <Paper sx={{ border: `1px solid ${theme.palette.border.main}`, mb: 2 }} elevation={0}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarnIcon sx={{ fontSize: 16, color: openRuleReviews.length > 0 ? theme.palette.warning.main : theme.palette.text.tertiary }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: theme.palette.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Rules Flagged for Review ({openRuleReviews.length})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {resolvedRuleReviews.length > 0 && (
              <Button
                size="small"
                onClick={() => setShowResolvedReviews((v) => !v)}
                sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}
              >
                {showResolvedReviews ? 'Hide' : 'Show'} resolved ({resolvedRuleReviews.length})
              </Button>
            )}
            <Tooltip title="Scan recent rule fire history for high false-positive rates">
              <Button
                size="small"
                variant="outlined"
                disabled={scanning}
                startIcon={scanning ? <CircularProgress size={13} color="inherit" /> : <ScanIcon sx={{ fontSize: 15 }} />}
                onClick={handleScanRules}
                sx={{ fontSize: '0.72rem', height: 28 }}
              >
                {scanning ? 'Scanning…' : 'Scan for issues'}
              </Button>
            </Tooltip>
          </Box>
        </Box>
        <Divider sx={{ borderColor: theme.palette.border.light }} />

        {ruleReviewsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : visibleRuleReviews.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3.5, px: 3, gap: 0.75 }}>
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem', fontWeight: 500 }}>
              No rules flagged for review
            </Typography>
            <Typography sx={{ color: theme.palette.text.tertiary, fontSize: '0.74rem', textAlign: 'center', maxWidth: 460 }}>
              A rule is flagged automatically when it fires repeatedly and the resulting recommendations are
              often marked false positive. Rules that should have fired but didn't aren't tracked yet — there's
              no record of what a rule "should" have matched against.
            </Typography>
          </Box>
        ) : (
          visibleRuleReviews.map((r) => (
            <RuleReviewRow
              key={r.id}
              review={r}
              clientName={clientMap[r.client_id]}
              onResolve={(status) => handleResolveRuleReview(r.id, status)}
            />
          ))
        )}
      </Paper>

      {loading && templates.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Custom templates */}
          <Paper sx={{ border: `1px solid ${theme.palette.border.main}` }} elevation={0}>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: theme.palette.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Custom Templates ({custom.length})
              </Typography>
            </Box>
            <Divider sx={{ borderColor: theme.palette.border.light }} />

            {custom.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, px: 3, gap: 1.5 }}>
                <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.88rem', fontWeight: 500 }}>
                  No custom templates yet
                </Typography>
                <Typography sx={{ color: theme.palette.text.tertiary, fontSize: '0.78rem', textAlign: 'center', maxWidth: 320 }}>
                  Create a template to define your own response workflow and auto-generate detection rules.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadIcon />}
                    onClick={() => setImportOpen(true)}
                    sx={{ fontSize: '0.76rem' }}
                  >
                    Import
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/playbooks/templates/new')}
                    sx={{ fontSize: '0.76rem' }}
                  >
                    Create template
                  </Button>
                </Box>
              </Box>
            ) : (
              custom.map((t) => (
                <TemplateRow
                  key={t.id}
                  template={t}
                  clientName={t.client_id ? clientMap[t.client_id] : undefined}
                  onEdit={() => navigate(`/playbooks/templates/${t.id}/edit`)}
                  onDelete={() => handleDelete(t.id)}
                  onGenerateRules={() => navigate(`/playbooks/templates/${t.id}/edit?tab=rules`)}
                />
              ))
            )}
          </Paper>

          {/* Built-in templates */}
          <Paper sx={{ border: `1px solid ${theme.palette.border.main}` }} elevation={0}>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: theme.palette.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Built-in Templates ({builtin.length})
              </Typography>
            </Box>
            <Divider sx={{ borderColor: theme.palette.border.light }} />
            {builtin.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                onEdit={() => {}}
                onDelete={() => {}}
                onGenerateRules={() => {}}
              />
            ))}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default PlaybookTemplatesPage;
